'use client'
import React, { useEffect, useRef, useState } from 'react'
import { ChevronUp, ChevronDown } from 'react-feather'
import { Button } from '@achieve/ascend'

// Tailwind classes and style constants for collapse states
const COLLAPSE_CLASSES = {
  base: 'transition-all duration-300 ease-in-out',
}

const COLLAPSE_STYLES = {
  collapsed: (height) => ({
    height: `${height}px`,
    maxHeight: `${height}px`,
    overflow: 'hidden',
  }),
  expanded: (contentHeight) => ({
    height: `${contentHeight}px`,
    maxHeight: `${contentHeight}px`,
    overflow: 'visible',
  }),
}

/**
 * A React component that provides a collapsible container based on a specified maximum height.
 *
 * @component
 * @param {Object} props - The properties of the component.
 * @param {ReactNode} props.children - The content to be collapsed/expanded.
 * @param {number} props.collapseHeight - The maximum height at which the content should be collapsed.
 * @param {string} [props.showMoreText='Show more'] - The text to display on the "Show more" button.
 * @param {string} [props.showLessText='Show less'] - The text to display on the "Show less" button.
 * @returns {JSX.Element} - The rendered JSX element.
 */
const CollapseByHeight = ({
  children,
  collapseHeight = 500,
  showMoreText = 'Show more',
  showLessText = 'Show less',
  className,
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [contentHeight, setContentHeight] = useState(0)
  const contentRef = useRef(null)

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight)
    }
  }, [children])

  const handleToggle = () => {
    setIsExpanded(!isExpanded)
  }

  const getCollapseClassName = () => {
    return COLLAPSE_CLASSES.base
  }

  const getCollapseStyles = () => {
    if (isExpanded) {
      return COLLAPSE_STYLES.expanded(contentHeight)
    } else {
      return COLLAPSE_STYLES.collapsed(collapseHeight)
    }
  }

  return (
    <div className={className} data-testid="collapse-by-height">
      <div className={getCollapseClassName()} style={getCollapseStyles()}>
        <div ref={contentRef}>{children}</div>
      </div>
      {contentHeight > Number(collapseHeight) && (
        <Button
          endIcon={isExpanded ? <ChevronUp /> : <ChevronDown />}
          className={'w-full mt-4 mb-8 mx-0'}
          onClick={handleToggle}
          variant="text"
          data-testid="collapse-expand-button"
        >
          {isExpanded ? showLessText : showMoreText}
        </Button>
      )}
    </div>
  )
}

export default CollapseByHeight
